import * as React from 'react'

import { api } from '@/lib/api'
import type { BillingStatus } from '@/lib/billing'

const DEFAULT_INTERVAL_MS = 2000
const DEFAULT_MAX_ATTEMPTS = 10

interface UseBillingActivationOptions {
  refetchStatus: () => Promise<{ data?: BillingStatus | undefined }>
  isDone: (status: BillingStatus | undefined) => boolean
  onSettled?: () => void
  intervalMs?: number
  maxAttempts?: number
}

interface UseBillingActivationResult {
  activating: boolean
  timedOut: boolean
  start: () => void
}

// Shared poller for "we just told Stripe/the API to do something and now
// need the DB to catch up" flows — post-checkout activation and plan
// changes both land via webhook, so the dashboard has to poll billing
// status rather than trust an immediate refetch. On timeout it falls back
// to POST /api/billing/sync once before giving up, so a slow webhook
// doesn't strand the UI on stale plan data.
export function useBillingActivation({
  refetchStatus,
  isDone,
  onSettled,
  intervalMs = DEFAULT_INTERVAL_MS,
  maxAttempts = DEFAULT_MAX_ATTEMPTS,
}: UseBillingActivationOptions): UseBillingActivationResult {
  const [activating, setActivating] = React.useState(false)
  const [timedOut, setTimedOut] = React.useState(false)
  const attemptsRef = React.useRef(0)
  const timerRef = React.useRef<ReturnType<typeof setInterval> | null>(null)

  const isDoneRef = React.useRef(isDone)
  isDoneRef.current = isDone
  const refetchStatusRef = React.useRef(refetchStatus)
  refetchStatusRef.current = refetchStatus
  const onSettledRef = React.useRef(onSettled)
  onSettledRef.current = onSettled

  const stop = React.useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const finish = React.useCallback((didTimeOut: boolean) => {
    stop()
    attemptsRef.current = 0
    setActivating(false)
    setTimedOut(didTimeOut)
    onSettledRef.current?.()
  }, [stop])

  const start = React.useCallback(() => {
    stop()
    attemptsRef.current = 0
    setTimedOut(false)
    setActivating(true)
  }, [stop])

  React.useEffect(() => {
    if (!activating) return

    timerRef.current = setInterval(() => {
      attemptsRef.current += 1
      void refetchStatusRef.current().then(async ({ data }) => {
        if (isDoneRef.current(data)) {
          finish(false)
          return
        }
        if (attemptsRef.current < maxAttempts) return

        stop()
        try {
          await api.post('/api/billing/sync', {})
        } catch {
          // Best-effort reconciliation — fall through to the re-check
          // regardless of whether sync itself succeeded (e.g. 404 when
          // the tenant genuinely has no subscription record yet).
        }
        const { data: resynced } = await refetchStatusRef.current()
        finish(!isDoneRef.current(resynced))
      })
    }, intervalMs)

    return () => stop()
  }, [activating, intervalMs, maxAttempts, finish, stop])

  return { activating, timedOut, start }
}
